import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';

export interface BreadcrumbItem {
  label: string;
  /** React Router path; if isReactRoute=false, target is on the Vue origin. */
  path: string;
  isReactRoute: boolean;
}

interface BreadcrumbContextValue {
  breadcrumbs: ReadonlyArray<BreadcrumbItem>;
  setBreadcrumbs: (items: ReadonlyArray<BreadcrumbItem>) => void;
  pushBreadcrumb: (item: BreadcrumbItem) => void;
  clearBreadcrumbs: () => void;
  /** Page-level actions rendered in the breadcrumb bar (e.g. Export CSV button). */
  breadcrumbActions: ReactNode;
  setBreadcrumbActions: (node: ReactNode) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(
  undefined
);

export const BreadcrumbProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<
    ReadonlyArray<BreadcrumbItem>
  >([]);
  const [breadcrumbActions, setBreadcrumbActions] = useState<ReactNode>(null);

  const pushBreadcrumb = useCallback((item: BreadcrumbItem): void => {
    setBreadcrumbs((prev) => [...prev, item]);
  }, []);

  const clearBreadcrumbs = useCallback((): void => {
    setBreadcrumbs([]);
  }, []);

  const value = useMemo<BreadcrumbContextValue>(
    () => ({
      breadcrumbs,
      setBreadcrumbs,
      pushBreadcrumb,
      clearBreadcrumbs,
      breadcrumbActions,
      setBreadcrumbActions,
    }),
    [breadcrumbs, setBreadcrumbs, pushBreadcrumb, clearBreadcrumbs, breadcrumbActions, setBreadcrumbActions]
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbs = (): BreadcrumbContextValue => {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error('useBreadcrumbs must be used inside <BreadcrumbProvider>');
  }
  return ctx;
};
